<script lang="ts">
  import type { TransactionPanelProps } from "./types";
  import { t } from "../../../helpers/translations";
  import { createGetIssueDetailsQuery } from "../../../queries/transactions";
  import TextNew from "../../TextNew.svelte";
  import BaseTransactionPanel from "./BaseTransactionPanel.svelte";
  import SectionHeader from "./SectionHeader.svelte";
  import IssuesImageCarousel from "../IssuesImageCarousel.svelte";
  import IssuesSkeleton from "../../skeletons/IssuesSkeleton.svelte";
  import NotesView from "./NotesView.svelte";
  import IconButton from "../../buttons/IconButton.svelte";
  import { noop } from "../../../helpers/utils";
  import { ISSUE_TYPES_MAP } from "./data";
  import type { Issue } from "../../../api/transactions";

  export let props: TransactionPanelProps;

  $: isResolved = props.transaction.is_resolved;
  $: issuesQuery = createGetIssueDetailsQuery(props.transaction.type, props.transaction.invoice_id);
  $: hasMatchingCredit = $issuesQuery.data && $issuesQuery.data.has_attached_credit;
  $: issues = $issuesQuery.data && $issuesQuery.data.issues;
  $: images =
    $issuesQuery.data &&
    [
      ...$issuesQuery.data?.images,
      ...($issuesQuery.data?.issues?.map((issue) => issue.images) ?? []),
    ].flat();

  function getTitle(issue: Issue) {
    if (!issue) return "";
    let title = ISSUE_TYPES_MAP[issue.type];

    if (issue.description) {
      title += ` - ${issue.description}`;
    }

    return title;
  }
</script>

<BaseTransactionPanel {...props}>
  <div
    class="header-actions"
    class:margin-right={props.onClose || props.onUnlinkTransaction}
    slot="header-actions"
  >
    {#if !hasMatchingCredit}
      <IconButton
        name="resolve"
        highlighted={isResolved}
        tooltip={{
          title: isResolved ? t("global.resolved.title") : t("tooltip.resolveNoRefund.title"),
          options: { placement: "left" },
        }}
        on:click={isResolved ? noop : props.onResolveTransaction}
      />
    {/if}
  </div>
  <div slot="top">
    {#if $issuesQuery.data?.description && !issues?.length}
      <NotesView title={$issuesQuery.data.description}>
        {#if images?.length}
          <div class="general-images">
            <IssuesImageCarousel {images} />
          </div>
        {/if}
      </NotesView>
    {/if}
  </div>
  <div class="middle" slot="middle">
    {#if issues?.length}
      <SectionHeader title={t("issues.reportedProducts.title")} borderBottom={!images?.length} />
    {/if}
    {#if $issuesQuery.isLoading}
      <IssuesSkeleton />
    {:else if issues?.length}
      {#if images?.length}
        <div class="images-container">
          <IssuesImageCarousel {images} />
        </div>
      {/if}

      {#each issues as issue}
        <div class="row">
          <TextNew size="s">{getTitle(issue)}</TextNew>
          <TextNew size="s" --rk-text-color={"var(--rk-text-low)"}
            >{`${issue.item_name} - ${issue.unit}`}</TextNew
          >
        </div>
      {/each}
    {/if}
  </div>
</BaseTransactionPanel>

<style>
  .header-actions {
    display: flex;
    align-items: center;
  }

  .margin-right {
    margin-right: 16px;
  }

  .images-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    justify-content: center;
    align-items: center;
    background-color: var(--rk-neutral-0);
    border: 1px solid var(--rk-divider-0);
    border-radius: 8px;
  }

  .middle {
    margin: 40px 0;
  }

  .images-container {
    padding: 42px 0 8px 0;
    overflow: hidden;
  }

  .general-images {
    padding: 16px 0 8px 0;
    margin-bottom: 24px;
  }

  .row {
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: space-between;
    border-bottom: 1px solid var(--rk-divider-0);
    padding: 16px 0;
  }
</style>
